export interface UsuarioUiThemeInterface {
  pageBackground: string;
  cardBackground: string;
  cardHeaderBackground: string;
  tableHeaderBackground: string;
  tableHeaderText: string;
  accentColor: string;
  sidebarBackground: string;
  sidebarActiveColor: string;
  /** light | dark — ativa estilos de texto/borda do tema escuro */
  themeMode?: 'light' | 'dark';
}

export const LIGHT_UI_THEME: UsuarioUiThemeInterface = {
  pageBackground: '#ffffff',
  cardBackground: '#ffffff',
  cardHeaderBackground: '#ffffff',
  tableHeaderBackground: '#f8f9fa',
  tableHeaderText: '#343a40',
  accentColor: '#ef8157',
  sidebarBackground: 'white',
  sidebarActiveColor: 'danger',
  themeMode: 'light'
};

export const DARK_UI_THEME: UsuarioUiThemeInterface = {
  pageBackground: '#0d0f12',
  cardBackground: '#1a1d24',
  cardHeaderBackground: '#232632',
  tableHeaderBackground: '#12141a',
  tableHeaderText: '#e8eaed',
  accentColor: '#51cbce',
  sidebarBackground: 'black',
  sidebarActiveColor: 'primary',
  themeMode: 'dark'
};

export const DEFAULT_UI_THEME: UsuarioUiThemeInterface = {
  pageBackground: '#f4f5f7',
  cardBackground: '#ffffff',
  cardHeaderBackground: '#ffffff',
  tableHeaderBackground: '#f8f9fa',
  tableHeaderText: '#343a40',
  accentColor: '#ef8157',
  sidebarBackground: 'white',
  sidebarActiveColor: 'danger',
  themeMode: 'light'
};

export interface UiThemeColorOption {
  id: string;
  label: string;
  value: string;
  cssClass?: string;
}

export const UI_THEME_PAGE_BACKGROUNDS: UiThemeColorOption[] = [
  { id: 'page-1', label: 'Cinza claro', value: '#f4f5f7' },
  { id: 'page-2', label: 'Azul suave', value: '#eef2f7' },
  { id: 'page-3', label: 'Branco', value: '#ffffff' },
  { id: 'page-4', label: 'Gelo', value: '#f8f9fa' },
  { id: 'page-5', label: 'Black', value: '#0d0f12' },
  { id: 'page-6', label: 'Grafite', value: '#121212' },
  { id: 'page-7', label: 'Carvão', value: '#1a1a1a' }
];

export const UI_THEME_CARD_BACKGROUNDS: UiThemeColorOption[] = [
  { id: 'card-1', label: 'Branco', value: '#ffffff' },
  { id: 'card-2', label: 'Cinza', value: '#f8fafc' },
  { id: 'card-3', label: 'Azul claro', value: '#f4f6ff' },
  { id: 'card-4', label: 'Black', value: '#1a1d24' },
  { id: 'card-5', label: 'Grafite', value: '#232632' },
  { id: 'card-6', label: 'Antracite', value: '#2c2f3a' }
];

export const UI_THEME_CARD_HEADER_BACKGROUNDS: UiThemeColorOption[] = [
  { id: 'ch-1', label: 'Branco', value: '#ffffff', cssClass: 'text-dark' },
  { id: 'ch-2', label: 'Cinza', value: '#f8f9fa', cssClass: 'text-dark' },
  { id: 'ch-3', label: 'Black', value: '#232632', cssClass: 'text-white' },
  { id: 'ch-4', label: 'Grafite', value: '#2c2f3a', cssClass: 'text-white' }
];

export const UI_THEME_TABLE_HEADERS: UiThemeColorOption[] = [
  { id: 'th-1', label: 'Cinza', value: '#f8f9fa', cssClass: 'text-dark' },
  { id: 'th-2', label: 'Roxo', value: '#5e72e4', cssClass: 'text-white' },
  { id: 'th-3', label: 'Escuro', value: '#343a40', cssClass: 'text-white' },
  { id: 'th-4', label: 'Accent', value: 'accent', cssClass: 'text-white' },
  { id: 'th-5', label: 'Black', value: '#12141a', cssClass: 'text-white' },
  { id: 'th-6', label: 'Preto', value: '#000000', cssClass: 'text-white' },
  { id: 'th-7', label: 'Grafite', value: '#1a1d24', cssClass: 'text-white' }
];

export const UI_THEME_ACCENT_PRESETS: UiThemeColorOption[] = [
  { id: 'acc-primary', label: 'Primary', value: '#51cbce' },
  { id: 'acc-info', label: 'Info', value: '#51bcda' },
  { id: 'acc-success', label: 'Success', value: '#6bd098' },
  { id: 'acc-warning', label: 'Warning', value: '#ffb236' },
  { id: 'acc-danger', label: 'Danger', value: '#ef8157' }
];

export const UI_THEME_SIDEBAR_BACKGROUNDS: UiThemeColorOption[] = [
  { id: 'sb-white', label: 'Claro', value: 'white' },
  { id: 'sb-brown', label: 'Escuro', value: 'brown' },
  { id: 'sb-black', label: 'Black', value: 'black' }
];

export interface UiThemePreset {
  id: string;
  label: string;
  theme: UsuarioUiThemeInterface;
  preview: {
    page: string;
    card: string;
    accent: string;
  };
}

const SOFT_BLUE_THEME: UsuarioUiThemeInterface = {
  pageBackground: '#eef2f7',
  cardBackground: '#ffffff',
  cardHeaderBackground: '#f8fafc',
  tableHeaderBackground: '#f8f9fa',
  tableHeaderText: '#343a40',
  accentColor: '#5e72e4',
  sidebarBackground: 'white',
  sidebarActiveColor: 'primary',
  themeMode: 'light'
};

/** Temas completos exibidos no painel de aparência. */
export const UI_THEME_PRESETS: UiThemePreset[] = [
  {
    id: 'default',
    label: 'Padrão',
    theme: { ...DEFAULT_UI_THEME },
    preview: { page: '#f4f5f7', card: '#ffffff', accent: '#ef8157' }
  },
  {
    id: 'white',
    label: 'Claro',
    theme: { ...LIGHT_UI_THEME },
    preview: { page: '#ffffff', card: '#ffffff', accent: '#ef8157' }
  },
  {
    id: 'black',
    label: 'Black',
    theme: { ...DARK_UI_THEME },
    preview: { page: '#0d0f12', card: '#1a1d24', accent: '#51cbce' }
  },
  {
    id: 'soft-blue',
    label: 'Azul',
    theme: { ...SOFT_BLUE_THEME },
    preview: { page: '#eef2f7', card: '#ffffff', accent: '#5e72e4' }
  }
];

const PAGE_TO_CARD_BG: Record<string, string> = {
  '#0d0f12': '#1a1d24',
  '#121212': '#1a1d24',
  '#1a1a1a': '#232632'
};

export function isDarkHexColor(value: string): boolean {
  if (!value || !value.startsWith('#') || value.length !== 7) {
    return false;
  }
  const r = parseInt(value.slice(1, 3), 16);
  const g = parseInt(value.slice(3, 5), 16);
  const b = parseInt(value.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.45;
}

/** Ajusta cards, cabeçalhos e tabelas conforme o fundo da página. */
export function buildSurfacesForPageBackground(pageBackground: string): Partial<UsuarioUiThemeInterface> {
  const dark = isDarkHexColor(pageBackground);
  if (dark) {
    const cardBackground = PAGE_TO_CARD_BG[pageBackground.toLowerCase()] || '#1a1d24';
    return {
      themeMode: 'dark',
      cardBackground,
      cardHeaderBackground: '#232632',
      tableHeaderBackground: '#12141a',
      tableHeaderText: '#e8eaed',
      sidebarBackground: 'black'
    };
  }

  const lightCards: Record<string, Partial<UsuarioUiThemeInterface>> = {
    '#ffffff': {
      cardBackground: '#ffffff',
      cardHeaderBackground: '#ffffff'
    },
    '#eef2f7': {
      cardBackground: '#ffffff',
      cardHeaderBackground: '#f8fafc'
    },
    '#f8f9fa': {
      cardBackground: '#ffffff',
      cardHeaderBackground: '#f8f9fa'
    }
  };

  const surfaces = lightCards[pageBackground.toLowerCase()] || {
    cardBackground: '#ffffff',
    cardHeaderBackground: '#ffffff'
  };

  return {
    themeMode: 'light',
    ...surfaces,
    tableHeaderBackground: '#f8f9fa',
    tableHeaderText: '#343a40',
    sidebarBackground: 'white'
  };
}
